/**
 * Пользователи. Модуль для работы с API
 * @module users
 */

import { maxPerPage } from "@/constants";
import { buildQueryParams, cleanParams } from "@/utils";

/**
 * Получение списка пользователей с пагинацией
 * @param {Object} params - параметры запроса
 * @returns {Promise} - данные пользователей
 * @throws {Error} - ошибка при получении данных
 */
export const getList = async (params) => {
  const cleanedFilters = cleanParams(params);
  const queryString = buildQueryParams(cleanedFilters);

  try {
    const response = await fetch(`/api/users?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение полного списка пользователей без пагинации
 * @param {Object} params - Параметры запроса
 * @returns {Promise} - список пользователей
 * @throws {Error} - ошибка при получении данных
 */
export const getUsersOptions = async (params) => {
  const cleanedFilters = cleanParams({
    ...params,
    page: 1,
    perpage: maxPerPage,
  });
  const queryString = new URLSearchParams(cleanedFilters).toString();

  try {
    const response = await fetch(`/api/users?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение данных пользователя
 * @param {string} uuid - UUID пользователя
 * @returns {Promise} - данные пользователя
 * @throws {Error} - ошибка при получении данных
 */
export const getItem = async (uuid) => {
  try {
    const response = await fetch(`/api/user?uuid=${uuid}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data.user;
  } catch (err) {
    throw err;
  }
};

/**
 * Создание пользователя
 * @param {Object} item - данные пользователя
 * @returns {Promise} - созданный пользователь
 * @throws {Error} - ошибка при создании данных
 */
export const createItem = async (item) => {
  try {
    const response = await fetch("/api/user", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при создании данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.user;
  } catch (err) {
    throw err;
  }
};

/**
 * Обновление данных пользователя
 * @param {Object} item - данные пользователя
 * @returns {Promise} - обновленный пользователь
 * @throws {Error} - ошибка при обновлении данных
 */
export const updateItem = async (item) => {
  try {
    const response = await fetch("/api/user", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.user;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление данных пользователя
 * @param {Object} item - данные пользователя
 * @returns {Promise} - удаленный пользователь
 * @throws {Error} - ошибка при удалении данных
 */
export const deleteItem = async (item) => {
  try {
    const response = await fetch("/api/user", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.user;
  } catch (err) {
    throw err;
  }
};

/**
 * Привязка сотрудника к пользователю
 * @param {Object} item - данные сотрудника
 * @param {string} item.user_uuid - UUID пользователя
 * @param {string} item.employee_uuid - UUID сотрудника
 * @returns {Promise} - результат привязки
 * @throws {Error} - ошибка при привязке данных
 */
export const bindingEmployee = async (item) => {
  try {
    const response = await fetch("/api/user/assignemployee", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при привязке сотрудника. ${data.error || "Попробуйте снова"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Отвязка сотрудника от пользователя
 * @param {Object} item - данные сотрудника
 * @param {string} item.user_uuid - UUID пользователя
 * @returns {Promise} - результат отвязки
 * @throws {Error} - ошибка при отвязке данных
 */
export const unbindingEmployee = async (item) => {
  try {
    const response = await fetch("/api/user/removeemployee", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при отвязке сотрудника. ${data.error || "Попробуйте снова"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Установить email для уведомлений
 * @param {Object} item - данные пользователя
 * @param {string} item.secondary_email - email пользователя
 * @param {string} item.uuid - UUID пользователя
 * @returns {Promise} - результат установки email
 * @throws {Error} - ошибка при установке email
 */
export const setEmailForNotifications = async (item) => {
  try {
    const response = await fetch("/api/user/email", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Установить настройку звуковой индикации уведомлений текущего пользователя
 * @param {boolean} enabled - включена ли звуковая индикация
 * @returns {Promise} - результат сохранения настройки
 * @throws {Error} - ошибка при сохранении настройки
 */
export const setSoundNotificationsEnabled = async (enabled) => {
  try {
    const response = await fetch("/api/user/sound-notifications", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ sound_notifications_enabled: enabled }),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при сохранении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};
